// src/components/Admin/SellerManagement/Notes/NoteItem.jsx
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader } from "../../../../components/ui/card";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import { Button } from "../../../../components/ui/button";
import { Textarea } from "../../../../components/ui/textarea";
import { useToast } from "../../../../hooks/use-toast";
import { format, parseISO } from 'date-fns'; // Requires: npm install date-fns

const NoteItem = ({ note, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  
  // Note: The backend schema should be updated to provide 'can_edit' and 'can_delete'
  // For now, we assume 'can_edit_delete' controls both.
  const { can_edit_delete: canModify } = note; 
  
  const [editContent, setEditContent] = useState(note.content);
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  const getInitials = (email) => {
    const parts = email.split('@')[0].split('.');
    return parts.map(part => part[0]).join('').toUpperCase().substring(0, 2);
  };
  
  const handleUpdate = async () => {
    if (editContent.trim() === note.content) {
      setIsEditing(false);
      return;
    }
    
    setIsUpdating(true);
    try {
      await onUpdate(note.id, editContent);
      setIsEditing(false);
      toast({ title: "Note updated successfully" });
    } catch (error) {
      toast({ title: "Failed to update note", variant: "destructive" });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this note? This action is permanent.")) {
      onDelete(note.id);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between p-4 space-y-0">
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{getInitials(note.author_email)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium leading-none">{note.author_email}</p>
            <p className="text-xs text-muted-foreground">
              {note.created_at_ist}
            </p>
          </div>
        </div>
        {canModify && !isEditing && (
          <div className="flex space-x-2">
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>Edit</Button>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={handleDelete}>Delete</Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={4}
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{note.content}</p>
        )}
      </CardContent>
      {isEditing && (
        <CardFooter className="flex justify-end space-x-2 p-4 pt-0">
          <Button variant="ghost" onClick={() => setIsEditing(false)}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={isUpdating}>
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default NoteItem;